import ArchiveIcon from 'icons/Archive';
import UserIcon from 'icons/User';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from 'icons/Home';
import { EditNote } from '@mui/icons-material';
import { CalendarMonth } from '@mui/icons-material';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { SystemClaimPermission } from 'src/types/claims';
import { PermissionLevel } from 'src/types/profilePermission';
import { JSX } from 'react';

interface ISectionItem {
  title: string;
  path: string;
  icon?: JSX.Element;
  children?: ISectionItem[];
}

export const NameMap: Record<string, string> = {
  '/inicio': 'Inicio',
  '/reportes': 'Reportes',
  '/servicios': 'Servicios',
  '/reportes/asistencia': 'Asistencia',
  '/reportes/jornada_diaria': 'Jornada Diaria',
  '/reportes/domingos_y_festivos': 'Domingos y Festivos',
  '/reportes/modificacion_programacion':
    'Modificaciones y/o alteraciones de turnos',
  '/reportes/estado_email': 'Estado Correos',
  '/anexos': 'Anexos',
  '/anexos/planilla_de_ruta': 'Planilla de Ruta',
  '/anexos/observaciones_planilla_de_ruta': 'Observaciones',
  '/anexos/programacion': 'Programación',
  '/anexos/resumen_trabajado': 'Resumen Trabajado',
  '/pactos/conductor/:id': 'Mis Pactos',
  '/pactos/pacto_hhee': 'Pactos HHEE',
  '/manejo_de_usuarios': 'Manejo de Usuarios',
  '/manejo_de_usuarios/usuarios': 'Usuarios',
  '/manejo_de_usuarios/perfiles': 'Perfiles',
  '/manejo_de_usuarios/documentos': 'Documentos',
  '/servicios/control_diario': 'Control Diario',
  '/reportes/incidentes': 'Incidentes',
  '/busqueda_de_asistencia': 'Buscar Asistencia',
  '/configuracion_correos': 'Configuración Correos',
  '/solicitudes_generales': 'Solicitudes Generales',
  '/carga_de_programacion': 'Carga de Programación',
  '/marcas_manuales': 'Marcas Manuales',
};

interface Section {
  title: string;
  items: ISectionItem[];
}

const getSections = (claims: SystemClaimPermission, id: string): Section[] => {
  const items: ISectionItem[] = [];

  items.push({
    title: NameMap['/inicio'],
    path: '/',
    icon: <HomeIcon fontSize="small" />,
  });

  if (claims.ManejodeUsuarios !== PermissionLevel.None) {
    const children: ISectionItem[] = [
      {
        title: NameMap['/manejo_de_usuarios/usuarios'],
        path: '/manejo_de_usuarios/usuarios',
      },
    ];
    if (claims.ManejodeUsuariosPerfiles !== PermissionLevel.None) {
      children.push({
        title: NameMap['/manejo_de_usuarios/perfiles'],
        path: '/manejo_de_usuarios/perfiles',
      });
    }
    if (claims.DocumentosTrabajador !== PermissionLevel.None) {
      children.push({
        title: NameMap['/manejo_de_usuarios/documentos'],
        path: '/manejo_de_usuarios/documentos',
      });
    }
    items.push({
      title: NameMap['/manejo_de_usuarios'],
      path: '/manejo_de_usuarios',
      icon: <UserIcon fontSize="small" />,
      children,
    });
  }

  // Configuración Correos
  if (claims.ConfiguracionCorreos !== PermissionLevel.None) {
    items.push({
      title: NameMap['/configuracion_correos'],
      path: '/configuracion_correos',
      icon: <ContactMailIcon fontSize="small" />,
    });
  }

  // Anexos
  const anexosChildren: ISectionItem[] = [];
  if (claims.PlanilladeRuta !== PermissionLevel.None) {
    anexosChildren.push({
      title: NameMap['/anexos/planilla_de_ruta'],
      path: '/anexos/planilla_de_ruta',
    });
  }
  if (claims.Programacion !== PermissionLevel.None) {
    anexosChildren.push({
      title: NameMap['/anexos/programacion'],
      path: '/anexos/programacion',
    });
  }
  if (claims.ResumenTrabajado !== PermissionLevel.None) {
    anexosChildren.push({
      title: NameMap['/anexos/resumen_trabajado'],
      path: '/anexos/resumen_trabajado',
    });
  }
  if (anexosChildren.length > 0) {
    items.push({
      title: NameMap['/anexos'],
      path: '/anexos',
      icon: <ArchiveIcon fontSize="small" />,
      children: anexosChildren,
    });
  }

  // Reportes
  const reportesChildren: ISectionItem[] = [];
  if (claims.Asistencia !== PermissionLevel.None) {
    reportesChildren.push({
      title: NameMap['/reportes/asistencia'],
      path: '/reportes/asistencia',
    });
  }
  if (claims.JornadaDiaria !== PermissionLevel.None) {
    reportesChildren.push({
      title: NameMap['/reportes/jornada_diaria'],
      path: '/reportes/jornada_diaria',
    });
  }
  if (claims.DomingosFestivos !== PermissionLevel.None) {
    reportesChildren.push({
      title: NameMap['/reportes/domingos_y_festivos'],
      path: '/reportes/domingos_y_festivos',
    });
  }
  if (claims.ModificacionProgramacion !== PermissionLevel.None) {
    reportesChildren.push({
      title: NameMap['/reportes/modificacion_programacion'],
      path: '/reportes/modificacion_programacion',
    });
  }
  if (claims.Incidentes !== PermissionLevel.None) {
    reportesChildren.push({
      title: NameMap['/reportes/incidentes'],
      path: '/reportes/incidentes',
    });
  }
  if (reportesChildren.length > 0) {
    items.push({
      title: NameMap['/reportes'],
      path: '/reportes',
      icon: <ArchiveIcon fontSize="small" />,
      children: reportesChildren,
    });
  }

  // Pactos
  if (claims.PactosHHEE !== PermissionLevel.None) {
    items.push({
      title: NameMap['/pactos/pacto_hhee'],
      path: '/pactos/pacto_hhee',
      icon: <ArchiveIcon fontSize="small" />,
    });
  }
  if (claims.MisPactos === PermissionLevel.ReadAndWrite) {
    items.push({
      title: NameMap['/pactos/conductor/:id'],
      path: `/pactos/conductor/${id}`,
      icon: <ArchiveIcon fontSize="small" />,
    });
  }

  // Servicios
  if (claims.ControlDiario !== PermissionLevel.None) {
    items.push({
      title: NameMap['/servicios/control_diario'],
      path: '/servicios/control_diario',
      icon: <ArchiveIcon fontSize="small" />,
    });
  }

  // Otros
  if (claims.BuscarAsistencia !== PermissionLevel.None) {
    items.push({
      title: NameMap['/busqueda_de_asistencia'],
      path: '/busqueda_de_asistencia',
      icon: <SearchIcon fontSize="small" />,
    });
  }
  if (claims.SolicitudesGenerales !== PermissionLevel.None) {
    items.push({
      title: NameMap['/solicitudes_generales'],
      path: '/solicitudes_generales',
      icon: <SearchIcon fontSize="small" />,
    });
  }
  if (claims.MarcasModificadas !== PermissionLevel.None) {
    items.push({
      title: NameMap['/marcas_manuales'],
      path: '/marcas_manuales',
      icon: <EditNote fontSize="small" />,
    });
  }
  if (claims.CargaProgramacion !== PermissionLevel.None) {
    items.push({
      title: NameMap['/carga_de_programacion'],
      path: '/carga_de_programacion',
      icon: <CalendarMonth fontSize="small" />,
    });
  }
  if (claims.ObservacionesPlanilladeRuta !== PermissionLevel.None) {
    items.push({
      title: NameMap['/anexos/observaciones_planilla_de_ruta'],
      path: '/anexos/observaciones_planilla_de_ruta',
      icon: <EditIcon fontSize="small" />,
    });
  }

  return [{ title: 'General', items }];
};

export default getSections;
